/**
 * SchemaMetadataStep - Collect schema metadata before creation
 *
 * Final step in schema creation wizard. Collects schema name,
 * compatibility mode, and description, then creates the schema.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSchemaMetadata, useSchemaCreationData } from '@/features/schemas/store/schemaCreationStore';
import { useCreateSchema } from '@/features/schemas/hooks/useSchemas';
import { validateSchemaName } from '@/features/schemas/utils/schemaValidation';
import { useToast } from '@/hooks/useToast';
import type { CompatibilityMode } from '@/features/schemas/types/schema.types';

/**
 * Compatibility mode options with descriptions
 */
const COMPATIBILITY_MODES: Array<{
  value: CompatibilityMode;
  label: string;
  description: string;
}> = [
  {
    value: 'backward',
    label: 'Backward Compatible',
    description: 'New schema versions can read data written with old versions',
  },
  {
    value: 'forward',
    label: 'Forward Compatible',
    description: 'Old schema versions can read data written with new versions',
  },
  {
    value: 'full',
    label: 'Full Compatibility',
    description: 'Both backward and forward compatible - maximum flexibility',
  },
  {
    value: 'none',
    label: 'No Compatibility',
    description: 'No compatibility guarantees - breaking changes allowed',
  },
];

/**
 * SchemaMetadataStep Component
 *
 * Final step of schema creation wizard. Collects metadata and creates schema.
 *
 * @example
 * ```tsx
 * <SchemaMetadataStep />
 * ```
 */
export const SchemaMetadataStep: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    schemaName,
    compatibilityMode,
    setSchemaName,
    setCompatibilityMode,
  } = useSchemaMetadata();

  const schemaData = useSchemaCreationData();
  const createSchema = useCreateSchema();

  const [touched, setTouched] = useState({
    name: false,
    compatibilityMode: false,
  });

  /**
   * Validate schema name
   */
  const nameErrors = useMemo(() => {
    if (!touched.name) return [];
    return validateSchemaName(schemaName);
  }, [schemaName, touched.name]);

  /**
   * Check if form is valid
   */
  const isValid = useMemo(() => {
    return (
      schemaName.trim().length >= 3 &&
      nameErrors.length === 0 &&
      compatibilityMode &&
      schemaData.fields.length > 0
    );
  }, [schemaName, nameErrors, compatibilityMode, schemaData.fields.length]);

  /**
   * Handle schema name change
   */
  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSchemaName(e.target.value);
      setTouched((prev) => ({ ...prev, name: true }));
    },
    [setSchemaName]
  );

  /**
   * Handle compatibility mode change
   */
  const handleCompatibilityChange = useCallback(
    (value: string) => {
      setCompatibilityMode(value as CompatibilityMode);
      setTouched((prev) => ({ ...prev, compatibilityMode: true }));
    },
    [setCompatibilityMode]
  );


  /**
   * Handle schema creation
   */
  const handleCreateSchema = useCallback(async () => {
    if (!isValid) {
      toast({
        title: 'Validation Error',
        description: 'Please fix all validation errors before creating the schema',
        variant: 'destructive',
      });
      return;
    }

    try {
      await createSchema.mutateAsync({
        name: schemaData.name,
        compatibility_mode: schemaData.compatibility_mode,
        fields: schemaData.fields,
        // Note: description is not supported in schema creation, only in metadata
      });

      toast({
        title: 'Schema Created',
        description: `Schema "${schemaData.name}" has been created successfully`,
      });

      // Navigate to schema library
      navigate('/schemas');
    } catch (error: any) {
      console.error('Failed to create schema:', error);

      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        'Failed to create schema. Please try again.';

      toast({
        title: 'Creation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  }, [isValid, schemaData, createSchema, toast, navigate]);

  /**
   * Get selected compatibility mode info
   */
  const selectedMode = useMemo(
    () => COMPATIBILITY_MODES.find((m) => m.value === compatibilityMode),
    [compatibilityMode]
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Schema Metadata</CardTitle>
          <CardDescription>
            Provide a name and compatibility settings for your schema
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Schema Name */}
          <div className="space-y-2">
            <Label htmlFor="schema-name">
              Schema Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="schema-name"
              placeholder="e.g., user_events, product_catalog"
              value={schemaName}
              onChange={handleNameChange}
              className={nameErrors.length > 0 ? 'border-destructive' : ''}
            />
            {nameErrors.length > 0 && (
              <div className="space-y-1">
                {nameErrors.map((error, index) => (
                  <p key={index} className="text-sm text-destructive">
                    {error.message}
                  </p>
                ))}
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              Must start with a letter and contain only letters, numbers, and underscores (no hyphens or spaces)
            </p>
          </div>

          {/* Compatibility Mode */}
          <div className="space-y-2">
            <Label htmlFor="compatibility-mode">
              Compatibility Mode <span className="text-destructive">*</span>
            </Label>
            <Select value={compatibilityMode} onValueChange={handleCompatibilityChange}>
              <SelectTrigger id="compatibility-mode">
                <SelectValue placeholder="Select compatibility mode" />
              </SelectTrigger>
              <SelectContent>
                {COMPATIBILITY_MODES.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>
                    <div className="flex flex-col">
                      <span className="font-medium">{mode.label}</span>
                      <span className="text-xs text-muted-foreground">{mode.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedMode && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  <strong>{selectedMode.label}:</strong> {selectedMode.description}
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Summary */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">Schema Summary:</p>
                <ul className="list-disc list-inside space-y-1 pl-2 text-sm">
                  <li>{schemaData.fields.length} fields defined</li>
                  <li>Compatibility mode: {compatibilityMode || 'Not selected'}</li>
                  <li>
                    Name: {schemaName.trim() || <em className="text-muted-foreground">Not set</em>}
                  </li>
                </ul>
              </div>
            </AlertDescription>
          </Alert>

          {/* Create Button */}
          <div className="pt-4">
            <Button
              onClick={handleCreateSchema}
              disabled={!isValid || createSchema.isPending}
              size="lg"
              className="w-full"
            >
              {createSchema.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Schema...
                </>
              ) : (
                'Create Schema'
              )}
            </Button>
          </div>

          {/* Instructions */}
          <div className="pt-4 space-y-2 text-sm text-muted-foreground border-t">
            <p className="font-medium">What happens next:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Schema will be registered in the catalog</li>
              <li>You can use this schema when creating datasets</li>
              <li>Schema versioning will be enabled based on compatibility mode</li>
              <li>You can view and manage the schema in the Schema Library</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
